/* eslint-disable n/handle-callback-err */
/* eslint-disable camelcase */
import React, { useState, useEffect, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  MenuItem,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  CircularProgress,
  Autocomplete,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { Save as SaveIcon, Search, LocationOn, PersonSearch, AssignmentTurnedIn, CloudOff } from "@mui/icons-material"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useQuery } from "@tanstack/react-query"
import { useMutation, api, showWarning, showSuccess } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Swal from "sweetalert2"
import localforage from "localforage"

import DocumentosAsociadosModal from "../../components/Global/DocumentosAsociadosModal/index"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1000px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EjecutarEncuesta = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const encuestaListada = state?.encuesta
  const enccodigo = encuestaListada?.enccodigo

  const [respuestas, setRespuestas] = useState({})
  const [cedulaBusqueda, setCedulaBusqueda] = useState("")
  const [fechaNacimientoBusqueda, setFechaNacimientoBusqueda] = useState("")
  const [nombresBusqueda, setNombresBusqueda] = useState("")

  // Estados de Ubicación Geográfica
  const [provinciaBusqueda, setProvinciaBusqueda] = useState("")
  const [cantonBusqueda, setCantonBusqueda] = useState("")
  const [parroquiaBusqueda, setParroquiaBusqueda] = useState("")
  const [zonaBusqueda, setZonaBusqueda] = useState("")

  const [geo, setGeo] = useState({ lat: null, lng: null, error: "Obteniendo ubicación..." })
  const [savedRespcodigo, setSavedRespcodigo] = useState(null)
  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false)

  const [isSearchingCNE, setIsSearchingCNE] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isOffline, setIsOffline] = useState(!navigator.onLine)

  // ==========================================================
  // INICIALIZACIÓN Y GPS
  // ==========================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => setGeo({ lat: position.coords.latitude, lng: position.coords.longitude, error: "" }),
        (error) => setGeo({ lat: null, lng: null, error: "Ubicación denegada o inactiva" }),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      )
    } else {
      setGeo({ lat: null, lng: null, error: "El navegador no soporta geolocalización" })
    }

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  // ==========================================================
  // CARGA DE CATÁLOGOS GEOGRÁFICOS EN CASCADA (React-Query)
  // ==========================================================
  const { data: rawProvincias = [], isFetching: isFetchingProv } = useQuery({
    queryKey: ["ubicacion_provincias_encuestas"],
    queryFn: async () => {
      if (isOffline) {
        const cache = await localforage.getItem("geo_provincias_cache")
        return cache || []
      }
      const res = await api.post("/ControlElectoral/getUbicaciones", { tipo: "PROVINCIA" })
      const data = res.data?.data || []
      if (data.length > 0) localforage.setItem("geo_provincias_cache", data)
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
  const listaProvincias = Array.isArray(rawProvincias)
    ? rawProvincias.map((i) => ({ id: String(i.id), label: i.label }))
    : []

  const { data: rawCantones = [], isFetching: isFetchingCant } = useQuery({
    queryKey: ["ubicacion_cantones_encuestas", provinciaBusqueda],
    queryFn: async () => {
      if (isOffline || !provinciaBusqueda) return []
      const res = await api.post("/ControlElectoral/getUbicaciones", { tipo: "CANTON", prov_codigo: provinciaBusqueda })
      return res.data?.data || []
    },
    enabled: !!provinciaBusqueda && !isOffline,
  })
  const listaCantones = Array.isArray(rawCantones) ? rawCantones.map((i) => ({ id: String(i.id), label: i.label })) : []

  const { data: rawParroquias = [], isFetching: isFetchingParr } = useQuery({
    queryKey: ["ubicacion_parroquias_encuestas", provinciaBusqueda, cantonBusqueda],
    queryFn: async () => {
      if (isOffline || !cantonBusqueda) return []
      const res = await api.post("/ControlElectoral/getUbicaciones", {
        tipo: "PARROQUIA",
        prov_codigo: provinciaBusqueda,
        cant_codigo: cantonBusqueda,
      })
      return res.data?.data || []
    },
    enabled: !!cantonBusqueda && !isOffline,
  })
  const listaParroquias = Array.isArray(rawParroquias)
    ? rawParroquias.map((i) => ({ id: String(i.id), label: i.label }))
    : []

  const { data: rawZonas = [], isFetching: isFetchingZona } = useQuery({
    queryKey: ["ubicacion_zonas_encuestas", provinciaBusqueda, cantonBusqueda, parroquiaBusqueda],
    queryFn: async () => {
      if (isOffline || !parroquiaBusqueda) return []
      const res = await api.post("/ControlElectoral/getUbicaciones", {
        tipo: "ZONA",
        prov_codigo: provinciaBusqueda,
        cant_codigo: cantonBusqueda,
        parr_codigo: parroquiaBusqueda,
      })
      return res.data?.data || []
    },
    enabled: !!parroquiaBusqueda && !isOffline,
  })
  const listaZonas = Array.isArray(rawZonas) ? rawZonas.map((i) => ({ id: String(i.id), label: i.label })) : []

  // ==========================================================
  // CARGA DE LA ENCUESTA
  // ==========================================================
  const { data: encuestaData, isLoading: isLoadingEnc } = useQuery({
    queryKey: ["getEstructuraEncuesta", enccodigo],
    queryFn: async () => {
      if (!enccodigo) return null
      const res = await api.post("/Encuestas/getEncuestaCompleta", { enccodigo })
      const payload = res?.success !== undefined ? res : res?.data
      if (payload && payload.success) return payload.data
      return null
    },
    enabled: !!enccodigo && !isOffline,
    refetchOnWindowFocus: false,
  })

  // ==========================================================
  // BÚSQUEDA CNE Y MAPEO DE COMBOS
  // ==========================================================
  const handleBuscarCNE = async () => {
    if (isOffline)
      return Swal.fire(
        "Modo Offline",
        "Búsqueda en CNE deshabilitada sin internet. Ingrese los datos manualmente.",
        "warning",
      )
    if (!cedulaBusqueda || cedulaBusqueda.length < 10)
      return Swal.fire({ icon: "warning", title: "Atención", text: "Digite una cédula válida de 10 dígitos" })

    setIsSearchingCNE(true)
    try {
      const res = await api.post("/EjecEncuestas/consultarCNE", { cedula: cedulaBusqueda })
      const isSuccess = res.success !== undefined ? res.success : res.data?.success
      const innerData = res.success !== undefined ? res.data : res.data?.data
      const message = res.message || res.data?.message

      if (isSuccess) {
        setNombresBusqueda(innerData.nombres || "")

        // Asignamos los nombres devueltos; el 'value' del Autocomplete validará el ID o hará fallback
        setProvinciaBusqueda(innerData.provincia?.includes("N/A") ? "" : innerData.provincia || "")
        setCantonBusqueda(innerData.canton === "N/A" ? "" : innerData.canton || "")
        setParroquiaBusqueda(innerData.parroquia === "N/A" ? "" : innerData.parroquia || "")
        setZonaBusqueda(innerData.zona === "N/A" ? "" : innerData.zona || "")

        try {
          if (innerData.fechaNacimiento) {
            const fechaSplit = String(innerData.fechaNacimiento).trim().split(" ")[0]
            if (fechaSplit.includes("/")) {
              const parts = fechaSplit.split("/")
              if (parts.length === 3)
                setFechaNacimientoBusqueda(`${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`)
            }
          } else setFechaNacimientoBusqueda("")
        } catch (e) {
          setFechaNacimientoBusqueda("")
        }

        if (message)
          Swal.fire({
            icon: "info",
            title: "Plan B Activado",
            text: message,
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          })
        else showSuccess("Datos obtenidos desde el Padrón Electoral")
      } else {
        Swal.fire({
          icon: "error",
          title: "No encontrado",
          text: message || "No se pudo consultar la cédula. Proceda a digitar manualmente.",
        })
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error de Red", text: "Problemas de conectividad con el servidor." })
    } finally {
      setIsSearchingCNE(false)
    }
  }

  const handleRespuestaChange = (pregsecuen, tipo, valor) => {
    setRespuestas((prev) => {
      if (tipo === "M") {
        const actuales = prev[pregsecuen] || []
        const index = actuales.indexOf(valor)
        const nuevos = [...actuales]
        if (index === -1) nuevos.push(valor)
        else nuevos.splice(index, 1)
        return { ...prev, [pregsecuen]: nuevos }
      }
      return { ...prev, [pregsecuen]: valor }
    })
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!encuestaData || !encuestaData.preguntas)
      return Swal.fire({ icon: "warning", title: "Atención", text: "La encuesta no tiene estructura válida." })

    const preguntas = encuestaData.preguntas
    const formateadas = []

    for (let i = 0; i < preguntas.length; i++) {
      const p = preguntas[i]
      const valor = respuestas[p.pregsecuen]

      if (p.pregobligatoria === 1) {
        if (p.pregtipo === "U" && (!valor || !valor.trim()))
          return Swal.fire({ icon: "warning", title: "Incompleto", text: `La pregunta #${i + 1} es obligatoria.` })
        if (p.pregtipo === "L" && !valor)
          return Swal.fire({
            icon: "warning",
            title: "Incompleto",
            text: `Seleccione una opción en la pregunta #${i + 1}.`,
          })
        if (p.pregtipo === "M" && (!valor || valor.length === 0))
          return Swal.fire({
            icon: "warning",
            title: "Incompleto",
            text: `Seleccione al menos una casilla en la pregunta #${i + 1}.`,
          })
      }

      if (valor) {
        if (p.pregtipo === "U") formateadas.push({ pregsecuen: p.pregsecuen, opcsecuen: 0, resptextolibre: valor })
        else if (p.pregtipo === "L")
          formateadas.push({ pregsecuen: p.pregsecuen, opcsecuen: valor, resptextolibre: null })
        else if (p.pregtipo === "M")
          valor.forEach((v) => formateadas.push({ pregsecuen: p.pregsecuen, opcsecuen: v, resptextolibre: null }))
      }
    }

    const payload = {
      enccodigo,
      audsecuen: encuestaListada.audsecuen || null,
      respcedula: cedulaBusqueda,
      respnombre: nombresBusqueda,
      respprovincia: provinciaBusqueda,
      respcanton: cantonBusqueda,
      respparroquia: parroquiaBusqueda,
      respzona: zonaBusqueda,
      resplatitud: geo.lat,
      resplongitud: geo.lng,
      respfecnac: fechaNacimientoBusqueda || null,
      respuestas: formateadas,
      timestamp: new Date().toISOString(),
    }

    if (isOffline) {
      const cola = (await localforage.getItem("encuestas_offline_queue")) || []
      cola.push(payload)
      await localforage.setItem("encuestas_offline_queue", cola)

      Swal.fire({
        icon: "warning",
        title: "Modo Fuera de Línea",
        text: "La encuesta se guardó en la memoria de su teléfono. No olvide sincronizar cuando recupere la señal.",
        confirmButtonColor: "#196C87",
      }).then(() => navigate(-1))
      return
    }

    setIsSaving(true)
    try {
      const res = await api.post("/EjecEncuestas/saveRespuestaEncuesta", payload)
      const responseData = res?.data !== undefined ? res.data : res

      if (responseData && responseData.success) {
        setSavedRespcodigo(responseData.respcodigo)
        Swal.fire({
          title: "¡Encuesta Procesada!",
          text: `Se generó el comprobante ${responseData.respcodigo}. ¿Desea adjuntar fotos o evidencias?`,
          icon: "success",
          showCancelButton: true,
          confirmButtonColor: "#196C87",
          cancelButtonColor: "#757575",
          confirmButtonText: "Sí, adjuntar fotos",
          cancelButtonText: "No, salir",
          allowOutsideClick: false,
        }).then((result) => {
          if (result.isConfirmed) setIsDocsModalOpen(true)
          else navigate(-1)
        })
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: responseData?.message || "Error al procesar la encuesta en el servidor.",
        })
      }
    } catch (error) {
      const cola = (await localforage.getItem("encuestas_offline_queue")) || []
      cola.push(payload)
      await localforage.setItem("encuestas_offline_queue", cola)

      Swal.fire({
        icon: "warning",
        title: "Red Inestable",
        text: "Fallo al enviar al servidor. La encuesta fue respaldada localmente en su dispositivo.",
        confirmButtonColor: "#196C87",
      }).then(() => navigate(-1))
    } finally {
      setIsSaving(false)
    }
  }

  const ejecutarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) => action?.acccaption === "GRABAR" || action?.acccaption === "EJECUTAR",
  )
  const toolbarActions = ejecutarAction
    ? [
        {
          label: ejecutarAction.acccaption,
          key: ejecutarAction.acccaption,
          icon: getIconComponent(ejecutarAction.accnameicono, ejecutarAction.acctipoico),
        },
      ]
    : [{ label: "FINALIZAR ENCUESTA", key: "GRABAR", icon: <SaveIcon /> }]

  if (!encuestaListada) {
    return (
      <ThemeProvider theme={theme}>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <BackIcon />
          <Alert severity="error" sx={{ mt: 3 }}>
            Regrese a la bandeja.
          </Alert>
        </div>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {isOffline && (
          <Alert severity="warning" icon={<CloudOff />} sx={{ mb: 2, borderRadius: 2 }}>
            <b>SIN CONEXIÓN:</b> Se guardarán los datos de forma local. No podrá usar la búsqueda del CNE.
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <span>
                <IconButton
                  onClick={handleSubmit}
                  disabled={isSaving || isSearchingCNE || isLoadingEnc}
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "white" }}
                >
                  {action.icon}
                </IconButton>
              </span>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 20px 30px",
            fontSize: "25px",
            color: "#196C87",
          }}
        >
          <AssignmentTurnedIn sx={{ fontSize: 32, mr: 1 }} />
          <b>Ejecución: {encuestaListada.enctitulo}</b>
        </div>

        <CustomBackdrop isLoading={isSaving || isLoadingEnc} />

        <Box sx={StyledRoot} component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={6}>
              <Alert icon={<LocationOn />} severity={geo.lat ? "success" : "warning"} sx={{ borderRadius: 2 }}>
                {geo.lat ? `Coordenadas capturadas: ${geo.lat.toFixed(5)}, ${geo.lng.toFixed(5)}` : geo.error}
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Encuesta {encuestaListada.encpublica === 1 ? "Pública (Libre)" : "Asignada Específicamente"}
              </Alert>
            </Grid>
          </Grid>

          <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2, borderLeft: "5px solid #196C87", bgcolor: "white" }}>
            <Typography
              variant="subtitle1"
              color="primary"
              fontWeight="bold"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <PersonSearch sx={{ mr: 1 }} /> Datos del Encuestado
            </Typography>

            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  label="Cédula de Identidad *"
                  size="small"
                  value={cedulaBusqueda}
                  onChange={(e) => setCedulaBusqueda(e.target.value.replace(/\D/g, ""))}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Nacimiento"
                  size="small"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={fechaNacimientoBusqueda}
                  onChange={(e) => setFechaNacimientoBusqueda(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBuscarCNE}
                  fullWidth
                  disabled={isSearchingCNE || cedulaBusqueda.length < 10 || isOffline}
                  sx={{ height: 40 }}
                >
                  {isSearchingCNE ? <CircularProgress size={20} color="inherit" /> : "BUSCAR"}
                </Button>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombres Completos *"
                  size="small"
                  value={nombresBusqueda}
                  onChange={(e) => setNombresBusqueda(e.target.value.toUpperCase())}
                  sx={{ bgcolor: "#e8f5e9", mb: 1 }}
                />
              </Grid>

              {/* ======================================================= */}
              {/* COMBOS GEOGRÁFICOS EN CASCADA (Autocompletes)           */}
              {/* ======================================================= */}
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  freeSolo
                  options={listaProvincias}
                  getOptionLabel={(o) => o.label || o.id || String(o)}
                  value={
                    listaProvincias.find(
                      (i) => i.id === provinciaBusqueda || i.label.toUpperCase() === provinciaBusqueda.toUpperCase(),
                    ) || (provinciaBusqueda ? { id: provinciaBusqueda, label: provinciaBusqueda } : null)
                  }
                  onChange={(e, newValue) => {
                    setProvinciaBusqueda(
                      typeof newValue === "string" ? newValue.toUpperCase() : newValue ? newValue.id : "",
                    )
                    setCantonBusqueda("")
                    setParroquiaBusqueda("")
                    setZonaBusqueda("")
                  }}
                  onInputChange={(e, newInputValue) => {
                    if (!e) setProvinciaBusqueda(newInputValue.toUpperCase())
                  }}
                  disabled={isSaving || isSearchingCNE || isFetchingProv}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Provincia"
                      size="small"
                      placeholder={isFetchingProv ? "Cargando..." : "Seleccione..."}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: isFetchingProv ? (
                          <CircularProgress size={20} />
                        ) : (
                          params.InputProps.startAdornment
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Autocomplete
                  freeSolo
                  options={listaCantones}
                  getOptionLabel={(o) => o.label || o.id || String(o)}
                  value={
                    listaCantones.find(
                      (i) => i.id === cantonBusqueda || i.label.toUpperCase() === cantonBusqueda.toUpperCase(),
                    ) || (cantonBusqueda ? { id: cantonBusqueda, label: cantonBusqueda } : null)
                  }
                  onChange={(e, newValue) => {
                    setCantonBusqueda(
                      typeof newValue === "string" ? newValue.toUpperCase() : newValue ? newValue.id : "",
                    )
                    setParroquiaBusqueda("")
                    setZonaBusqueda("")
                  }}
                  onInputChange={(e, newInputValue) => {
                    if (!e) setCantonBusqueda(newInputValue.toUpperCase())
                  }}
                  disabled={isSaving || isSearchingCNE || isFetchingCant || (!provinciaBusqueda && !isOffline)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cantón"
                      size="small"
                      placeholder={
                        !provinciaBusqueda ? "Seleccione provincia" : isFetchingCant ? "Cargando..." : "Seleccione..."
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: isFetchingCant ? (
                          <CircularProgress size={20} />
                        ) : (
                          params.InputProps.startAdornment
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Autocomplete
                  freeSolo
                  options={listaParroquias}
                  getOptionLabel={(o) => o.label || o.id || String(o)}
                  value={
                    listaParroquias.find(
                      (i) => i.id === parroquiaBusqueda || i.label.toUpperCase() === parroquiaBusqueda.toUpperCase(),
                    ) || (parroquiaBusqueda ? { id: parroquiaBusqueda, label: parroquiaBusqueda } : null)
                  }
                  onChange={(e, newValue) => {
                    setParroquiaBusqueda(
                      typeof newValue === "string" ? newValue.toUpperCase() : newValue ? newValue.id : "",
                    )
                    setZonaBusqueda("")
                  }}
                  onInputChange={(e, newInputValue) => {
                    if (!e) setParroquiaBusqueda(newInputValue.toUpperCase())
                  }}
                  disabled={isSaving || isSearchingCNE || isFetchingParr || (!cantonBusqueda && !isOffline)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Parroquia"
                      size="small"
                      placeholder={
                        !cantonBusqueda ? "Seleccione cantón" : isFetchingParr ? "Cargando..." : "Seleccione..."
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: isFetchingParr ? (
                          <CircularProgress size={20} />
                        ) : (
                          params.InputProps.startAdornment
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Autocomplete
                  freeSolo
                  options={listaZonas}
                  getOptionLabel={(o) => o.label || o.id || String(o)}
                  value={
                    listaZonas.find(
                      (i) => i.id === zonaBusqueda || i.label.toUpperCase() === zonaBusqueda.toUpperCase(),
                    ) || (zonaBusqueda ? { id: zonaBusqueda, label: zonaBusqueda } : null)
                  }
                  onChange={(e, newValue) => {
                    setZonaBusqueda(typeof newValue === "string" ? newValue.toUpperCase() : newValue ? newValue.id : "")
                  }}
                  onInputChange={(e, newInputValue) => {
                    if (!e) setZonaBusqueda(newInputValue.toUpperCase())
                  }}
                  disabled={isSaving || isSearchingCNE || isFetchingZona || (!parroquiaBusqueda && !isOffline)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Zona / Recinto"
                      size="small"
                      placeholder={
                        !parroquiaBusqueda ? "Seleccione parroquia" : isFetchingZona ? "Cargando..." : "Seleccione..."
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: isFetchingZona ? (
                          <CircularProgress size={20} />
                        ) : (
                          params.InputProps.startAdornment
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
            Cuestionario
          </Typography>

          {encuestaData?.preguntas?.map((preg, index) => {
            const opciones = encuestaData.opciones.filter((o) => o.pregsecuen === preg.pregsecuen)
            const valorActual = respuestas[preg.pregsecuen] || (preg.pregtipo === "M" ? [] : "")

            return (
              <Card
                key={preg.pregsecuen}
                elevation={1}
                sx={{ mb: 3, borderLeft: preg.pregobligatoria === 1 ? "4px solid #f57c00" : "4px solid #4caf50" }}
              >
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    {index + 1}. {preg.pregtexto}{" "}
                    {preg.pregobligatoria === 1 && <span style={{ color: "red" }}>*</span>}
                  </Typography>

                  {preg.pregtipo === "U" && (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Escriba la respuesta aquí..."
                      value={valorActual}
                      onChange={(e) => handleRespuestaChange(preg.pregsecuen, "U", e.target.value)}
                    />
                  )}
                  {preg.pregtipo === "L" && (
                    <TextField
                      select
                      fullWidth
                      label="Seleccione una opción"
                      value={valorActual}
                      onChange={(e) => handleRespuestaChange(preg.pregsecuen, "L", e.target.value)}
                    >
                      <MenuItem value="">
                        <em>-- Seleccionar --</em>
                      </MenuItem>
                      {opciones.map((o) => (
                        <MenuItem key={o.opcsecuen} value={o.opcsecuen}>
                          {o.opctexto}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {preg.pregtipo === "M" && (
                    <FormGroup>
                      <Grid container spacing={1}>
                        {opciones.map((o) => (
                          <Grid item xs={12} sm={6} key={o.opcsecuen}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Array.isArray(valorActual) && valorActual.includes(o.opcsecuen)}
                                  onChange={() => handleRespuestaChange(preg.pregsecuen, "M", o.opcsecuen)}
                                  color="primary"
                                />
                              }
                              label={o.opctexto}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </FormGroup>
                  )}
                </CardContent>
              </Card>
            )
          })}

          <Box mt={4} textAlign="center">
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={isSaving || isSearchingCNE}
              startIcon={isOffline ? <CloudOff /> : <SaveIcon />}
              sx={{ minWidth: 200, py: 1.5, borderRadius: 2 }}
            >
              {isOffline ? "GUARDAR EN EL TELÉFONO" : "FINALIZAR Y ENVIAR ENCUESTA"}
            </Button>
          </Box>
        </Box>
      </div>

      <DocumentosAsociadosModal
        isOpen={isDocsModalOpen}
        onClose={() => {
          setIsDocsModalOpen(false)
          navigate(-1)
        }}
        contexto={{ docqgenero: savedRespcodigo, docprocqgenero: "enccrespuestas", docsecuen: 1 }}
        onSuccess={() => console.log("Fotos guardadas")}
      />
    </ThemeProvider>
  )
}

export default EjecutarEncuesta
